import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Search, MapPin, Star, Award, Tag, ChevronRight, Briefcase,
  Utensils, Home, Car, Heart, Sparkles, Building,
  Dumbbell, GraduationCap, PawPrint, Music, ShoppingBag, DollarSign,
  Laptop, Hammer, Scale, Stethoscope, Globe, Coffee, Plane, ArrowRight,
  Users, MessageSquare, Clock, Eye, UserPlus, FileText, Calendar, 
  Image, Video, Download, Ticket, Shield, Zap, User, Rocket,
  Wrench, Droplets, Plug, Paintbrush, TreeDeciduous, Thermometer,
  HardHat, Brush, CreditCard, KeyRound, Lightbulb,
  DoorOpen, Lock, Sun, Bug, Warehouse, Waves, Building2,
  Megaphone, Target, PenTool, BarChart3, Mail, Share2, Palette,
  Code, Camera, Headphones, FileVideo, Mic, Globe2, TrendingUp,
  BadgeCheck, Crown, Handshake, Gift, ExternalLink, Copy, Check, Play, Film,
  Menu, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Branding
const DIRECTORY_LOGO = "https://customer-assets.emergentagent.com/job_horsepowered/artifacts/9jgbu523_website%20badge.png";

// TAP Dispatch - 20 services (10 per row), Credit Firm at end as green
const TAP_DISPATCH_SERVICES = [
  // Row 1 - 10 services
  { name: 'Mortgage Lender', icon: DollarSign, category: 'financial' },
  { name: 'Realtor', icon: KeyRound, category: 'real-estate' },
  { name: 'HVAC', icon: Thermometer, category: 'home-services' },
  { name: 'Roofing', icon: Home, category: 'home-services' },
  { name: 'Flooring', icon: HardHat, category: 'home-services' },
  { name: 'Cleaning', icon: Brush, category: 'home-services' },
  { name: 'Painters', icon: Paintbrush, category: 'home-services' },
  { name: 'Construction', icon: Hammer, category: 'construction' },
  { name: 'Handyman', icon: Wrench, category: 'home-services' },
  { name: 'Plumbers', icon: Droplets, category: 'home-services' },
  // Row 2 - 10 services (including new ones and Get Loan Ready at end)
  { name: 'Electricians', icon: Plug, category: 'home-services' },
  { name: 'Landscaping', icon: TreeDeciduous, category: 'home-services' },
  { name: 'Windows & Doors', icon: DoorOpen, category: 'home-services' },
  { name: 'Smart Home', icon: Lock, category: 'technology' },
  { name: 'Solar & Energy', icon: Sun, category: 'home-services' },
  { name: 'Pest Control', icon: Bug, category: 'home-services' },
  { name: 'Garage Doors', icon: Warehouse, category: 'home-services' },
  { name: 'Pool & Spa', icon: Waves, category: 'home-services' },
  { name: 'Foundation', icon: Building2, category: 'construction' },
  { name: 'Get Loan Ready', icon: CreditCard, category: 'financial', link: 'https://creditfirminc.net/credit-boost-624266?am_id=roehubbard3909', isExternal: true, isGreen: true },
];

// POD Department Services - 40+ vetted experts
const POD_DEPARTMENTS = [
  { name: 'AI Marketing', icon: Target, department: 'marketing' },
  { name: 'Social Media', icon: Share2, department: 'marketing' },
  { name: 'Content Writing', icon: PenTool, department: 'content' },
  { name: 'SEO Expert', icon: TrendingUp, department: 'marketing' },
  { name: 'Email Marketing', icon: Mail, department: 'marketing' },
  { name: 'Ads Management', icon: Megaphone, department: 'advertising' },
  { name: 'Brand Design', icon: Palette, department: 'design' },
  { name: 'Web Development', icon: Code, department: 'technology' },
  { name: 'Video Production', icon: FileVideo, department: 'media' },
  { name: 'Photography', icon: Camera, department: 'media' },
  { name: 'Podcast Host', icon: Mic, department: 'media' },
  { name: 'Virtual Assistant', icon: Headphones, department: 'operations' },
  { name: 'CRM Setup', icon: BarChart3, department: 'technology' },
  { name: 'AI Automation', icon: Zap, department: 'technology' },
  { name: 'Sales Funnels', icon: Target, department: 'marketing' },
  { name: 'Copywriting', icon: FileText, department: 'content' },
];

// Category icons mapping
const CATEGORY_ICONS = {
  'restaurants': Utensils,
  'home-services': Home,
  'automotive': Car,
  'health-wellness': Heart,
  'beauty': Sparkles,
  'professional-services': Briefcase,
  'real-estate': Building,
  'fitness': Dumbbell,
  'education': GraduationCap,
  'pet-services': PawPrint,
  'entertainment': Music,
  'retail': ShoppingBag,
  'financial': DollarSign,
  'technology': Laptop,
  'construction': Hammer,
  'legal': Scale,
  'medical': Stethoscope,
  'ecommerce': Globe,
  'food-beverage': Coffee,
  'travel-hospitality': Plane,
};

// Content type icons
const CONTENT_ICONS = {
  discussion: MessageSquare,
  community_article: Users,
  blog_article: FileText,
  soundcloud_post: Music,
  classified: Tag,
  coupon: Ticket,
  event: Calendar,
  job_listing: Briefcase,
  photo_album: Image,
  digital_product: Download,
  product: ShoppingBag,
  property: Home,
  video: Video,
};

// Star Rating Component
function StarRating({ rating, size = 'sm' }) {
  const sizeClass = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${star <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );
}

// Business Card - Light background with Bookmark functionality
function BusinessCard({ business }) {
  const Icon = CATEGORY_ICONS[business.category] || Globe;
  const [isBookmarked, setIsBookmarked] = React.useState(false);
  const [bookmarkLoading, setBookmarkLoading] = React.useState(false);
  
  // Check if user is logged in
  const token = localStorage.getItem('token');
  
  const handleBookmarkToggle = async (e) => {
    e.preventDefault(); // Prevent navigating to business page
    e.stopPropagation();
    
    if (!token) {
      window.location.href = '/directory/register';
      return;
    }
    
    setBookmarkLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/social/bookmarks/toggle`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ business_id: business.id })
      });
      
      if (response.ok) {
        const data = await response.json();
        setIsBookmarked(data.bookmarked);
      }
    } catch (error) {
      console.error('Bookmark toggle failed:', error);
    } finally {
      setBookmarkLoading(false);
    }
  };
  
  return (
    <Link to={`/directory/business/${business.id}`}>
      <motion.div
        whileHover={{ y: -4, boxShadow: '0 12px 24px rgba(0,0,0,0.15)' }}
        className="bg-white rounded-xl p-5 transition-all h-full border border-gray-100 hover:border-cyan-200 relative"
      >
        {/* Bookmark Button */}
        <button
          onClick={handleBookmarkToggle}
          disabled={bookmarkLoading}
          className={`absolute top-3 right-3 p-1.5 rounded-full transition-all z-10 ${
            isBookmarked 
              ? 'bg-red-50 text-red-500 hover:bg-red-100' 
              : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-red-400'
          }`}
          data-testid={`bookmark-btn-${business.id}`}
        >
          <Heart className={`w-4 h-4 ${isBookmarked ? 'fill-current' : ''}`} />
        </button>

        <div className="flex items-start gap-3 mb-3 pr-8">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center flex-shrink-0">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 text-[15px] leading-tight line-clamp-1">{business.name}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{business.subcategory}</p>
          </div>
          {business.is_ai_certified && (
            <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-50 rounded-full border border-amber-200">
              <Award className="w-3 h-3 text-amber-500" />
              <span className="text-[10px] text-amber-700 font-medium">AI Certified</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 mb-2.5">
          <StarRating rating={Math.round(business.rating)} />
          <span className="text-sm text-gray-800 font-medium">{business.rating}</span>
          <span className="text-xs text-gray-400">({business.review_count})</span>
        </div>

        <p className="text-sm text-gray-600 line-clamp-2 mb-3 leading-relaxed">{business.description}</p>

        <div className="flex items-center gap-1.5 text-gray-500 text-xs">
          <MapPin className="w-3.5 h-3.5" />
          <span>{business.city}, {business.state}</span>
        </div>
      </motion.div>
    </Link>
  );
}

// Content Feed Card - Blue background
function ContentCard({ content }) {
  const Icon = CONTENT_ICONS[content.content_type] || FileText;
  
  return (
    <motion.div
      whileHover={{ x: 2 }}
      className="bg-cyan-800/50 rounded-lg p-3.5 border border-cyan-600/30 hover:border-cyan-500/50 transition-all"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-cyan-600 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-white text-sm line-clamp-1">{content.title}</h4>
          <p className="text-xs text-cyan-200 mt-0.5">by {content.author_name}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2.5 text-xs text-cyan-300">
        <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {content.views || 0}</span>
        <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {content.likes || 0}</span>
      </div>
    </motion.div>
  );
}

// New Member Card - Blue background
function NewMemberCard({ member }) {
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-cyan-600/30 last:border-0">
      <div className="w-9 h-9 rounded-full bg-cyan-600 flex items-center justify-center text-white font-semibold text-sm">
        {member.name.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-white text-sm truncate">{member.name}</p>
        {member.company_name && (
          <p className="text-xs text-cyan-200 truncate">{member.company_name}</p>
        )}
      </div>
      {member.is_ai_certified && (
        <Award className="w-4 h-4 text-amber-400 flex-shrink-0" />
      )}
    </div>
  );
}

// Sleek Round TAP Button Component
function TapButton({ service, onClick }) {
  const Icon = service.icon;
  
  // Green button for Get Loan Ready
  if (service.isGreen) {
    if (service.isExternal && service.link) {
      return (
        <a href={service.link} target="_blank" rel="noopener noreferrer">
          <motion.button
            whileHover={{ scale: 1.08, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="flex flex-col items-center gap-2 p-3 transition-all group"
          >
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:shadow-emerald-500/50 border border-emerald-400 transition-all">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <span className="text-[11px] font-medium text-emerald-400 group-hover:text-emerald-300 text-center leading-tight transition-colors">{service.name}</span>
          </motion.button>
        </a>
      );
    }
  }
  
  // External link button
  if (service.isExternal && service.link) {
    return (
      <a href={service.link} target="_blank" rel="noopener noreferrer">
        <motion.button
          whileHover={{ scale: 1.08, y: -2 }}
          whileTap={{ scale: 0.95 }}
          className="flex flex-col items-center gap-2 p-3 transition-all group"
        >
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center shadow-lg shadow-black/30 group-hover:shadow-cyan-500/20 border border-slate-600 group-hover:border-cyan-400/50 transition-all">
            <Icon className="w-6 h-6 text-cyan-400 group-hover:text-cyan-300" />
          </div>
          <span className="text-[11px] font-medium text-gray-400 group-hover:text-white text-center leading-tight transition-colors">{service.name}</span>
        </motion.button>
      </a>
    );
  }
  
  // Regular button
  return (
    <motion.button
      whileHover={{ scale: 1.08, y: -2 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => onClick(service)}
      className="flex flex-col items-center gap-2 p-3 transition-all group"
    >
      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center shadow-lg shadow-black/30 group-hover:shadow-cyan-500/20 border border-slate-600 group-hover:border-cyan-400/50 transition-all">
        <Icon className="w-6 h-6 text-cyan-400 group-hover:text-cyan-300" />
      </div>
      <span className="text-[11px] font-medium text-gray-400 group-hover:text-white text-center leading-tight transition-colors">{service.name}</span>
    </motion.button>
  );
}

// POD Department Button - Requires login
function PODButton({ service, onClick }) {
  const Icon = service.icon;
  
  return (
    <motion.button
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onClick(service)}
      className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gradient-to-br from-purple-900/50 to-indigo-900/50 border border-purple-500/30 hover:border-purple-400/50 transition-all group"
    >
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg group-hover:shadow-purple-500/30 transition-all">
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-[10px] font-medium text-purple-200 group-hover:text-white text-center leading-tight transition-colors">{service.name}</span>
      <div className="flex items-center gap-1">
        <BadgeCheck className="w-3 h-3 text-amber-400" />
        <span className="text-[8px] text-amber-400">AI Certified</span>
      </div>
    </motion.button>
  );
}

export default function DirectoryPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [bestOfBest, setBestOfBest] = useState([]);
  const [streamingContent, setStreamingContent] = useState({});
  const [promotedStream, setPromotedStream] = useState([]);
  const [stats, setStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [contentFeed, setContentFeed] = useState([]);
  const [newMembers, setNewMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [catsRes, featuredRes, statsRes, couponsRes, contentRes, membersRes, bestRes, streamRes, promotedRes] = await Promise.all([
        fetch(`${API_URL}/api/directory/categories`),
        fetch(`${API_URL}/api/directory/businesses/featured?limit=8`),
        fetch(`${API_URL}/api/directory/stats`),
        fetch(`${API_URL}/api/directory/coupons?limit=4`),
        fetch(`${API_URL}/api/directory/content/feed?limit=5`),
        fetch(`${API_URL}/api/directory/members/new?limit=5`),
        fetch(`${API_URL}/api/directory/best-of-best`),
        fetch(`${API_URL}/api/directory/streaming-content`),
        fetch(`${API_URL}/api/directory/content/stream?limit=20`)
      ]);
      
      setCategories(await catsRes.json());
      setFeaturedBusinesses(await featuredRes.json());
      setStats(await statsRes.json());
      setCoupons(await couponsRes.json());
      setContentFeed(await contentRes.json());
      setNewMembers(await membersRes.json());
      setBestOfBest(await bestRes.json());
      setStreamingContent(await streamRes.json());
      
      // Get promoted content stream
      if (promotedRes.ok) {
        const promotedData = await promotedRes.json();
        setPromotedStream(promotedData.stream || []);
      }
    } catch (error) {
      console.error('Failed to load directory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/directory/search?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleTapDispatch = (service) => {
    window.location.href = `/directory/category/${service.category}?service=${encodeURIComponent(service.name)}`;
  };

  const handlePODClick = (service) => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/marketplace');
    } else {
      navigate('/login?redirect=/marketplace');
    }
  };

  // Split services into two rows of 10
  const row1Services = TAP_DISPATCH_SERVICES.slice(0, 10);
  const row2Services = TAP_DISPATCH_SERVICES.slice(10, 20);

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Navigation - Dark */}
      <nav className="sticky top-0 z-50 bg-[#0d1320]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={DIRECTORY_LOGO} alt="AI Horse-Powered" className="w-10 h-10 rounded-lg" />
              <div className="hidden sm:block">
                <span className="font-semibold text-white text-[15px]">AI Horse-Powered</span>
                <p className="text-[11px] text-gray-500">Publishing Site</p>
              </div>
            </Link>
            
            <div className="flex items-center gap-6">
              {/* Desktop Menu */}
              <div className="hidden md:flex items-center gap-5">
                <Link to="/directory/deals" className="text-gray-400 hover:text-white text-sm transition-colors">Deals</Link>
                <Link to="/directory/jobs" className="text-gray-400 hover:text-white text-sm transition-colors">Jobs</Link>
                <Link to="/directory/classifieds" className="text-gray-400 hover:text-white text-sm transition-colors">Classifieds</Link>
                <a href="https://aihorsepowered.com/ai-besty-link" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 text-sm transition-colors font-medium">
                  Voted Besty
                </a>
                <a href="https://aihorsepowered.com" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 text-sm transition-colors font-medium flex items-center gap-1">
                  $120K Grant <Award className="w-3.5 h-3.5" />
                </a>
              </div>
              
              {/* Desktop Buttons */}
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white hover:bg-white/10">
                    Enter
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white border-0">
                    <UserPlus className="w-4 h-4 mr-1" /> Join
                  </Button>
                </Link>
              </div>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-gray-400 hover:text-white transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-white/10 py-4"
            >
              <div className="flex flex-col gap-3">
                <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-white py-2 px-2 rounded-lg hover:bg-white/5 transition-colors">
                  How it Works
                </a>
                <a href="#departments" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-white py-2 px-2 rounded-lg hover:bg-white/5 transition-colors">
                  Departments
                </a>
                <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="text-cyan-400 hover:text-cyan-300 py-2 px-2 rounded-lg hover:bg-white/5 transition-colors font-medium">
                  Pricing
                </Link>
                <div className="border-t border-white/10 my-2"></div>
                <Link to="/directory/deals" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-white py-2 px-2 rounded-lg hover:bg-white/5 transition-colors">
                  Deals
                </Link>
                <Link to="/directory/jobs" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-white py-2 px-2 rounded-lg hover:bg-white/5 transition-colors">
                  Jobs
                </Link>
                <Link to="/directory/classifieds" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-white py-2 px-2 rounded-lg hover:bg-white/5 transition-colors">
                  Classifieds
                </Link>
                <a href="https://aihorsepowered.com/ai-besty-link" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 py-2 px-2 rounded-lg hover:bg-white/5 transition-colors font-medium">
                  Voted Besty
                </a>
                <a href="https://aihorsepowered.com" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 py-2 px-2 rounded-lg hover:bg-white/5 transition-colors font-medium flex items-center gap-1">
                  $120K Grant <Award className="w-3.5 h-3.5" />
                </a>
                <div className="border-t border-white/10 my-2"></div>
                <div className="flex gap-2 px-2">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full border-gray-600 text-gray-300 hover:bg-white/10">
                      Enter
                    </Button>
                  </Link>
                  <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="flex-1">
                    <Button size="sm" className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white border-0">
                      Join
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section - Compact */}
      <section className="relative py-8 lg:py-12 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-950/20 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/5 blur-[120px] rounded-full" />
        
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            {/* Logo & Title */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <img 
                src={DIRECTORY_LOGO} 
                alt="One Force Live" 
                className="w-20 h-20 rounded-xl shadow-lg shadow-cyan-500/30 border border-cyan-500/20"
              />
              <div className="text-left">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight">
                  One Force Live
                </h1>
                <p className="text-cyan-400 font-medium">Publishing Site</p>
              </div>
            </div>
            
            <p className="text-base text-gray-400 mb-8 max-w-xl mx-auto">
              Discover 1000s of verified businesses. Read real reviews. Get exclusive deals.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="What are you looking for?"
                  className="w-full pl-14 pr-36 py-4 text-[16px] bg-white rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 shadow-xl shadow-black/20"
                  data-testid="directory-search-input"
                />
                <Button 
                  type="submit" 
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-6 py-2.5 rounded-lg font-medium"
                  data-testid="directory-search-btn"
                >
                  Search
                </Button>
              </div>
            </form>

            {/* Stats - Compact */}
            <div className="flex items-center justify-center gap-6 sm:gap-10 mb-2">
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-bold text-white">1000s</p>
                <p className="text-[10px] text-gray-500">Businesses</p>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-bold text-amber-400">{stats?.ai_certified_businesses || '50'}+</p>
                <p className="text-[10px] text-gray-500">AI Certified</p>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div className="text-center">
                <p className="text-xl sm:text-2xl font-bold text-emerald-400">{stats?.active_coupons || '200'}+</p>
                <p className="text-[10px] text-gray-500">Deals</p>
              </div>
              <div className="w-px h-8 bg-white/10 hidden sm:block" />
              <div className="text-center hidden sm:block">
                <p className="text-xl sm:text-2xl font-bold text-white">{stats?.total_reviews?.toLocaleString() || '5,000'}+</p>
                <p className="text-[10px] text-gray-500">Reviews</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TAP Dispatch Section - Full Width Below Hero */}
      <section className="py-8 px-4 bg-gradient-to-b from-[#0d1320] to-[#0a0f1a] border-y border-white/5">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full border border-amber-500/30 mb-3">
              <Zap className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-semibold text-amber-400">TAP Dispatch</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-1">TAP a Button & Save Time & Money!</h2>
            <p className="text-sm text-gray-500">Find the Best of Best in your local area instantly. These pros work together — trusted network!</p>
          </div>

          {/* Finger Tap Animation */}
          <div className="flex justify-center mb-4">
            <motion.img 
              src="https://static.prod-images.emergentagent.com/jobs/89351914-cb5a-4c13-9a4f-f44202b8873a/images/76df1b1eecff277dfb76de9be9fb458d79c16eeb11ce42119b4fb916d72f5650.png"
              alt="Tap to select"
              className="w-20 h-20 object-contain"
              animate={{ 
                y: [0, 8, 0],
                scale: [1, 0.95, 1]
              }}
              transition={{ 
                duration: 1.2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          </div>

          {/* Row 1 - 10 buttons */}
          <div className="flex flex-wrap justify-center gap-1 sm:gap-2 mb-2">
            {row1Services.map((service) => (
              <TapButton 
                key={service.name} 
                service={service} 
                onClick={handleTapDispatch}
              />
            ))}
          </div>

          {/* Row 2 - 10 buttons (including Get Loan Ready at end) */}
          <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
            {row2Services.map((service) => (
              <TapButton 
                key={service.name} 
                service={service} 
                onClick={handleTapDispatch}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Mr Epic Characters - Decorative Section */}
      <section className="py-6 px-4 bg-gradient-to-r from-cyan-950/10 via-purple-950/10 to-cyan-950/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-center items-center gap-4 md:gap-8 flex-wrap">
            <motion.img 
              src="/images/mr-epic-1.png"
              alt="Mr Epic"
              className="w-24 md:w-32 h-auto object-contain rounded-xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            />
            <motion.img 
              src="/images/mr-epic-2.png"
              alt="Mr Epic Action"
              className="w-24 md:w-32 h-auto object-contain rounded-xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            />
            <motion.div 
              className="text-center px-4"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <p className="text-lg md:text-xl font-bold text-white">Your AI-Powered Business Hub</p>
              <p className="text-sm text-cyan-400">One Force Live • Publishing Site</p>
            </motion.div>
            <motion.img 
              src="/images/mr-epic-3.png"
              alt="Mr Epic Hero"
              className="w-24 md:w-32 h-auto object-contain rounded-xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            />
            <motion.img 
              src="/images/mr-epic-4.png"
              alt="Mr Epic Power"
              className="w-24 md:w-32 h-auto object-contain rounded-xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            />
          </div>
        </div>
      </section>

      {/* Best of Best Section - Voted Top Businesses */}
      {bestOfBest.length > 0 && (
        <section className="py-10 px-4 bg-gradient-to-b from-amber-950/20 via-[#0a0f1a] to-[#0a0f1a] border-y border-amber-500/10">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Voted Best of Best</h2>
                  <p className="text-sm text-amber-400/80">AI Certified & Community Voted Top Businesses</p>
                </div>
              </div>
              <Link to="/directory/search?ai_certified=true" className="text-amber-400 hover:text-amber-300 text-sm flex items-center gap-1">
                View all <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {bestOfBest.slice(0, 12).map((business, index) => (
                <Link key={business.id} to={`/directory/business/${business.id}`}>
                  <motion.div 
                    whileHover={{ y: -4, scale: 1.02 }}
                    className="bg-gradient-to-br from-amber-900/30 to-orange-900/20 rounded-xl p-4 border border-amber-500/20 hover:border-amber-500/40 transition-all"
                  >
                    <div className="relative mb-3">
                      <div className="w-full h-20 rounded-lg bg-gradient-to-br from-amber-800/50 to-orange-800/50 flex items-center justify-center overflow-hidden">
                        {business.logo || business.image ? (
                          <img 
                            src={business.logo || business.image} 
                            alt={business.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`w-full h-full flex items-center justify-center ${business.logo || business.image ? 'hidden' : ''}`}>
                          <span className="text-2xl font-bold text-amber-400/70">{business.name?.charAt(0) || 'B'}</span>
                        </div>
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                        <Crown className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    <h3 className="text-white font-medium text-sm truncate">{business.name}</h3>
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                      <span className="text-xs text-amber-400">{business.rating || '5.0'}</span>
                      <span className="text-xs text-gray-500">• {business.votes || 0} votes</span>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Streaming Content Section */}
      {Object.keys(streamingContent).length > 0 && (
        <section className="py-10 px-4 bg-[#0a0f1a]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center">
                <Play className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Streaming Content</h2>
                <p className="text-sm text-gray-400">Latest from our Best of Best businesses</p>
              </div>
            </div>
            
            {/* Cinematic Ads */}
            {streamingContent.cinematic_ad?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                  <Film className="w-4 h-4" /> Cinematic Ads
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {streamingContent.cinematic_ad.slice(0, 3).map((item) => (
                    <motion.div 
                      key={item.id}
                      whileHover={{ scale: 1.02 }}
                      className="bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-cyan-500/30 transition-all"
                    >
                      {item.video_url ? (
                        <video src={item.video_url} className="w-full h-40 object-cover" muted loop playsInline />
                      ) : (
                        <div className="w-full h-40 bg-gradient-to-br from-cyan-900/50 to-teal-900/50 flex items-center justify-center">
                          <Film className="w-12 h-12 text-cyan-500/50" />
                        </div>
                      )}
                      <div className="p-3">
                        <h4 className="text-white text-sm font-medium truncate">{item.title}</h4>
                        <p className="text-gray-500 text-xs mt-1">{item.business_name}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Videos */}
            {streamingContent.video?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-purple-400 mb-4 flex items-center gap-2">
                  <Video className="w-4 h-4" /> Videos
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {streamingContent.video.slice(0, 6).map((item) => (
                    <motion.div 
                      key={item.id}
                      whileHover={{ scale: 1.05 }}
                      className="bg-white/5 rounded-lg overflow-hidden border border-white/10"
                    >
                      <div className="w-full h-24 bg-purple-900/30 flex items-center justify-center">
                        <Video className="w-8 h-8 text-purple-500/50" />
                      </div>
                      <p className="text-white text-xs p-2 truncate">{item.title}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Blog Articles */}
            {streamingContent.blog_article?.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-emerald-400 mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Blog Articles
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {streamingContent.blog_article.slice(0, 3).map((item) => (
                    <motion.div 
                      key={item.id}
                      whileHover={{ y: -2 }}
                      className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-emerald-500/30 transition-all"
                    >
                      <h4 className="text-white font-medium text-sm mb-2">{item.title}</h4>
                      <p className="text-gray-400 text-xs line-clamp-2">{item.excerpt || item.content?.substring(0, 100)}</p>
                      <p className="text-emerald-400 text-xs mt-2">{item.business_name}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Promoted Content Stream - Latest from Businesses */}
      {promotedStream.length > 0 && (
        <section className="py-10 px-4 bg-gradient-to-b from-[#0a0f1a] to-[#0d1525]">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <Megaphone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Latest from Businesses</h2>
                  <p className="text-sm text-gray-400">Promoted content from our directory</p>
                </div>
              </div>
              <Link to="/directory/content" className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1">
                View all <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Content Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {promotedStream.slice(0, 8).map((content) => {
                const ContentIcon = CONTENT_ICONS[content.content_type] || FileText;
                return (
                  <Link 
                    key={content.id}
                    to={`/directory/content/${content.id}`}
                    data-testid={`promoted-content-${content.id}`}
                  >
                    <motion.div 
                      whileHover={{ y: -4, scale: 1.02 }}
                      className="bg-white/5 rounded-xl overflow-hidden border border-white/10 hover:border-purple-500/30 transition-all group h-full"
                    >
                      {/* Content Image/Thumbnail */}
                      {content.images?.[0] ? (
                        <img 
                          src={content.images[0]} 
                          alt={content.title}
                          className="w-full h-32 object-cover"
                        />
                      ) : content.video_url ? (
                        <div className="w-full h-32 bg-gradient-to-br from-purple-900/50 to-pink-900/50 flex items-center justify-center">
                          <Play className="w-10 h-10 text-white/50 group-hover:text-white transition-colors" />
                        </div>
                      ) : (
                        <div className="w-full h-32 bg-gradient-to-br from-purple-900/30 to-pink-900/30 flex items-center justify-center">
                          <ContentIcon className="w-10 h-10 text-purple-500/50" />
                        </div>
                      )}
                      
                      {/* Content Info */}
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400 capitalize">
                            {content.content_type.replace(/_/g, ' ')}
                          </span>
                          {content.is_featured && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-400">
                              Featured
                            </span>
                          )}
                        </div>
                        <h3 className="text-white font-medium text-sm mb-1 line-clamp-2 group-hover:text-purple-400 transition-colors">
                          {content.title}
                        </h3>
                        <p className="text-gray-500 text-xs line-clamp-2 mb-2">{content.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-purple-400 text-xs">{content.business_name || content.author_name}</span>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" /> {content.views || 0}
                            </span>
                            <span className="flex items-center gap-1">
                              <Heart className="w-3 h-3" /> {content.likes || 0}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-12">
        
        {/* Quick Categories - Light cards on dark background */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-white">Popular Categories</h2>
              <p className="text-sm text-gray-500 mt-1">Browse businesses by what they do best</p>
            </div>
            <Link to="/directory/search" className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
            {categories.slice(0, 10).map((category) => {
              const Icon = CATEGORY_ICONS[category.slug] || Globe;
              return (
                <Link 
                  key={category.slug} 
                  to={`/directory/category/${category.slug}`}
                >
                  <motion.div 
                    whileHover={{ y: -2 }}
                    className="bg-white rounded-xl p-4 text-center hover:shadow-lg transition-all border border-gray-100 hover:border-cyan-200"
                  >
                    <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-cyan-50 to-teal-50 flex items-center justify-center mx-auto mb-2.5">
                      <Icon className="w-5 h-5 text-cyan-600" />
                    </div>
                    <h3 className="font-medium text-gray-900 text-sm">{category.name.split(' ')[0]}</h3>
                  </motion.div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* POD Departments Section */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <Crown className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">40+ POD Departments</h2>
                <p className="text-sm text-gray-500">Vetted AI Certified Experts • Co-Op Pricing</p>
              </div>
            </div>
            <Link to="/marketplace" className="text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {/* Section description */}
          <p className="text-xs text-purple-400/70 italic mb-6 flex items-center gap-2">
            <Lightbulb className="w-3 h-3" />
            "These are not freelancers — they are vetted experts who have AI Horse-Powered their careers under our brand and bylaws."
          </p>

          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-3">
            {POD_DEPARTMENTS.map((service) => (
              <PODButton 
                key={service.name} 
                service={service} 
                onClick={handlePODClick}
              />
            ))}
          </div>
          
          {/* CTA */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-400 mb-3">
              Best results at fastest speeds • Most discounted Co-Op prices
            </p>
            <Link to="/register">
              <Button className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white">
                <BadgeCheck className="w-4 h-4 mr-2" /> Hire an AI Certified Expert
              </Button>
            </Link>
          </div>
        </section>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-12">
            
            {/* AI Certified - Best of Best */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">Best of Best</h2>
                    <p className="text-sm text-gray-500">AI Certified businesses you can trust</p>
                  </div>
                </div>
                <Link to="/directory/search?ai_certified=true" className="text-amber-400 hover:text-amber-300 text-sm flex items-center gap-1">
                  View all <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              
              {/* Section Guide */}
              <p className="text-xs text-amber-400/70 italic mb-6 flex items-center gap-2">
                <Lightbulb className="w-3 h-3" />
                "Quality is not an act, it is a habit." — Aristotle
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {featuredBusinesses.filter(b => b.is_ai_certified).slice(0, 4).map((business) => (
                  <BusinessCard key={business.id} business={business} />
                ))}
              </div>
            </section>

            {/* Affiliate Programs Section */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
                    <Handshake className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">Best of Best Affiliate Programs</h2>
                    <p className="text-sm text-gray-500">Partner with top businesses & earn</p>
                  </div>
                </div>
              </div>
              
              {/* Section Guide */}
              <p className="text-xs text-pink-400/70 italic mb-6 flex items-center gap-2">
                <Lightbulb className="w-3 h-3" />
                "Build your empire by helping others build theirs."
              </p>

              {/* Affiliate Program Promo Card */}
              <div className="bg-gradient-to-br from-pink-900/30 to-rose-900/30 rounded-2xl p-6 border border-pink-500/30">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center flex-shrink-0">
                    <Gift className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2">Launch Your Affiliate Program</h3>
                    <p className="text-sm text-pink-200 mb-4">
                      Let us build your custom affiliate program! We'll promote it to thousands of users and worldwide through our directory, TV, Radio, Social Media, Meta, Google, TikTok, and more.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="px-3 py-1 bg-pink-500/20 rounded-full text-xs text-pink-300 border border-pink-500/30">
                        <Globe2 className="w-3 h-3 inline mr-1" /> Worldwide Reach
                      </span>
                      <span className="px-3 py-1 bg-pink-500/20 rounded-full text-xs text-pink-300 border border-pink-500/30">
                        <TrendingUp className="w-3 h-3 inline mr-1" /> Multi-Platform
                      </span>
                      <span className="px-3 py-1 bg-pink-500/20 rounded-full text-xs text-pink-300 border border-pink-500/30">
                        <Users className="w-3 h-3 inline mr-1" /> 1000s of Users
                      </span>
                    </div>
                    <Link to="/register">
                      <Button className="bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white">
                        <Megaphone className="w-4 h-4 mr-2" /> Start Your Affiliate Program
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </section>

            {/* All Businesses */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-semibold text-white">Featured Businesses</h2>
                <Link to="/directory/search" className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1">
                  Browse all <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
              
              {/* Section Guide */}
              <p className="text-xs text-cyan-400/70 italic mb-6 flex items-center gap-2">
                <Lightbulb className="w-3 h-3" />
                "Support local. Shop small. Build community."
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                {featuredBusinesses.slice(0, 4).map((business) => (
                  <BusinessCard key={business.id} business={business} />
                ))}
              </div>
            </section>

            {/* Deals Section */}
            {coupons.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
                      <Tag className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-white">Today's Deals</h2>
                      <p className="text-sm text-gray-500">Save with exclusive offers</p>
                    </div>
                  </div>
                  <Link to="/directory/deals" className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center gap-1">
                    All deals <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
                
                {/* Section Guide */}
                <p className="text-xs text-emerald-400/70 italic mb-6 flex items-center gap-2">
                  <Lightbulb className="w-3 h-3" />
                  "A penny saved is a penny earned." — Benjamin Franklin
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  {coupons.map((coupon) => (
                    <motion.div
                      key={coupon.id}
                      whileHover={{ y: -2 }}
                      className="bg-white rounded-xl p-5 border border-gray-100 hover:border-emerald-200 transition-all"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-[15px]">{coupon.title}</h3>
                          <p className="text-sm text-gray-500">{coupon.business_name}</p>
                        </div>
                        <div className="px-2.5 py-1 bg-emerald-50 rounded-lg border border-emerald-100">
                          <span className="text-emerald-700 font-bold text-sm">
                            {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`}
                          </span>
                        </div>
                      </div>
                      {coupon.code && (
                        <code className="inline-block px-3 py-1.5 bg-gray-50 border border-gray-200 rounded text-emerald-600 text-sm font-mono mt-2">
                          {coupon.code}
                        </code>
                      )}
                    </motion.div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar - Blue themed */}
          <div className="space-y-6">
            
            {/* AI Ask Widget - Blue */}
            <div className="bg-gradient-to-br from-cyan-700 to-teal-800 rounded-xl p-5 border border-cyan-500/30 shadow-lg">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-5 h-5 text-cyan-200" />
                <h3 className="font-semibold text-white">AI Directory Assistant</h3>
              </div>
              <p className="text-sm text-cyan-100 mb-4">
                Ask me anything! I can help you find the perfect business for your needs.
              </p>
              <Link to="/register">
                <Button className="w-full bg-white hover:bg-gray-100 text-cyan-700 font-semibold">
                  <Sparkles className="w-4 h-4 mr-2" /> Try AI Search
                </Button>
              </Link>
            </div>

            {/* Why Choose Us - Blue */}
            <div className="bg-gradient-to-br from-cyan-700 to-teal-800 rounded-xl p-5 border border-cyan-500/30 shadow-lg">
              <h3 className="font-semibold text-white mb-4">Why AI Horse-Powered?</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-white text-sm">AI Verified</p>
                    <p className="text-xs text-cyan-200">All businesses are AI-vetted for quality</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center flex-shrink-0">
                    <Award className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-white text-sm">Best of Best Badge</p>
                    <p className="text-xs text-cyan-200">Top rated businesses get certified</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-white text-sm">Instant Deals</p>
                    <p className="text-xs text-cyan-200">Exclusive discounts from local businesses</p>
                  </div>
                </div>
              </div>
            </div>

            {/* New Members - Blue */}
            <div className="bg-gradient-to-br from-cyan-700 to-teal-800 rounded-xl p-5 border border-cyan-500/30 shadow-lg">
              <div className="flex items-center gap-2 mb-4">
                <UserPlus className="w-4 h-4 text-cyan-200" />
                <h3 className="font-semibold text-white">New Members</h3>
              </div>
              {newMembers.length > 0 ? (
                <div>
                  {newMembers.map((member) => (
                    <NewMemberCard key={member.id} member={member} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-cyan-200 text-center py-4">No new members yet</p>
              )}
            </div>

            {/* Latest Content - Blue */}
            <div className="bg-gradient-to-br from-cyan-700 to-teal-800 rounded-xl p-5 border border-cyan-500/30 shadow-lg">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4 text-cyan-200" />
                <h3 className="font-semibold text-white">Latest Content</h3>
              </div>
              {contentFeed.length > 0 ? (
                <div className="space-y-3">
                  {contentFeed.map((content) => (
                    <ContentCard key={content.id} content={content} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-cyan-200 text-center py-4">No content yet</p>
              )}
            </div>

            {/* Job Board CTA - Blue */}
            <div className="bg-gradient-to-br from-cyan-700 to-teal-800 rounded-xl p-5 border border-cyan-500/30 shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-bl-full" />
              <div className="relative">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center mb-3">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-white mb-1">Job Board</h3>
                <p className="text-sm text-cyan-100 mb-4">Find your next opportunity in the AI Horse-Powered network.</p>
                <Link to="/directory/jobs">
                  <Button className="w-full bg-white hover:bg-gray-100 text-cyan-700 font-semibold">
                    Browse Jobs <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Partner Plan CTA */}
            <div className="bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-xl p-5 border border-cyan-400/30 shadow-lg relative overflow-hidden">
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full" />
              <div className="relative">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center mb-3">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-white mb-1">Get Platform Access</h3>
                <p className="text-sm text-cyan-100 mb-2">
                  Start your <span className="font-bold text-white">30-day free trial</span> today!
                </p>
                <p className="text-xs text-cyan-200 mb-4">
                  Full platform access • $120K Co-Op Funds • AI Workforce
                </p>
                <Link to="/register">
                  <Button className="w-full bg-white hover:bg-gray-100 text-cyan-700 font-bold">
                    Join Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Start Business / Affiliate CTA */}
            <div className="bg-gradient-to-br from-amber-600 to-orange-700 rounded-xl p-5 border border-amber-500/30 shadow-lg relative overflow-hidden">
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full" />
              <div className="relative">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center mb-3">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-white mb-2">Earn as an Affiliate!</h3>
                <p className="text-sm text-amber-100 mb-4">
                  Join our affiliate program and earn commissions promoting local businesses.
                </p>
                <div className="space-y-2">
                  <Link to="/register">
                    <Button className="w-full bg-white hover:bg-gray-100 text-amber-700 font-semibold">
                      <Rocket className="w-4 h-4 mr-2" /> Become an Affiliate
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Consumer Join CTA */}
            <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl p-5 border border-purple-500/30 shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-bl-full" />
              <div className="relative">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center mb-3">
                  <User className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-white mb-2">Join as a Member</h3>
                <p className="text-sm text-purple-100 mb-4">
                  Bookmark businesses, write reviews, access deals & chat with our AI assistant!
                </p>
                <Link to="/register">
                  <Button className="w-full bg-white hover:bg-gray-100 text-purple-700 font-semibold">
                    <UserPlus className="w-4 h-4 mr-2" /> Join Now
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section - Dark with gradient accent */}
      <section className="py-16 px-4 bg-gradient-to-b from-[#0a0f1a] to-[#0d1525] border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-amber-400/70 text-sm italic mb-4">
            "Alone we can do so little; together we can do so much." — Helen Keller
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Own a business? Start your 30-day free trial!
          </h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Join 1000s of businesses. Get found by customers, 
            access the platform, earn as an affiliate, and grow.
          </p>
          <p className="text-cyan-400 text-sm mb-8">
            30-day free trial • Full platform access • $120K Co-Op Funds
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold px-8 py-3 text-[15px]">
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 px-8 py-3 text-[15px]">
                <Award className="w-4 h-4 mr-2" /> View All Plans
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Badge Widget Section - For Business Owners */}
      <section className="py-16 px-4 bg-gradient-to-b from-amber-950/20 to-black">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_daa57298-04ae-460b-8f69-e638243862e1/artifacts/svw0chlf_website%20badge.png"
              alt="Voted Best of Best Badge"
              className="w-32 h-auto mx-auto mb-6"
            />
            <h2 className="text-3xl font-bold text-white mb-3">
              Show Off Your <span className="text-amber-400">Best of Best</span> Badge
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Add the Voted Best of Best badge to your website and let your customers know you&apos;re part of the 
              AI-certified local business network. When visitors click your badge, they&apos;ll see your full profile 
              and discover other top-rated businesses in your area.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Badge Preview */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 text-center"
            >
              <p className="text-gray-600 text-sm mb-4">Preview on your website:</p>
              <div className="bg-gray-100 rounded-xl p-8 inline-block">
                <img 
                  src="https://customer-assets.emergentagent.com/job_daa57298-04ae-460b-8f69-e638243862e1/artifacts/svw0chlf_website%20badge.png"
                  alt="Voted Best of Best Badge"
                  className="w-48 h-auto mx-auto"
                />
                <p className="text-gray-500 text-xs mt-3">Proud member of the Best of Best Directory</p>
              </div>
            </motion.div>

            {/* Instructions & Code */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bg-gray-800/50 rounded-xl p-6 border border-amber-500/20">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Code className="w-5 h-5 text-amber-400" />
                  How to Add Your Badge
                </h3>
                <ol className="space-y-3 text-sm text-gray-300">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs font-bold shrink-0">1</span>
                    <span>Sign up or log in to your business account</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs font-bold shrink-0">2</span>
                    <span>Go to your Business Profile settings</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs font-bold shrink-0">3</span>
                    <span>Click &quot;Get Badge Code&quot; to generate your unique embed code</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs font-bold shrink-0">4</span>
                    <span>Copy the code and paste it into your website&apos;s HTML</span>
                  </li>
                </ol>
              </div>

              <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                <h4 className="font-semibold text-white mb-3 text-sm">Sample Embed Code</h4>
                <div className="bg-black/50 rounded-lg p-4 font-mono text-xs text-gray-400 overflow-x-auto">
                  <code>
                    {`<a href="https://your-badge-link" target="_blank">
  <img src="badge-url" alt="Voted Best of Best" style="width:200px" />
</a>`}
                  </code>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Your personalized badge code will include your unique business ID
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://aihorsepowered.com" target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-semibold py-3">
                    Get Qualified for Best of Best Badge & $120K Grant <Award className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <Link to="/login" className="flex-1">
                  <Button variant="outline" className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                    Enter Dashboard
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 grid sm:grid-cols-3 gap-6"
          >
            {[
              { icon: Users, title: 'Drive Local Traffic', desc: 'Badge links to your profile + nearby businesses' },
              { icon: Award, title: 'Build Trust', desc: 'Show customers you&apos;re AI-certified' },
              { icon: TrendingUp, title: 'Boost SEO', desc: 'Backlink from our directory improves rankings' },
            ].map((benefit, i) => (
              <div key={i} className="bg-gray-800/30 rounded-xl p-5 border border-gray-700/50 text-center">
                <benefit.icon className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                <h4 className="font-semibold text-white mb-1">{benefit.title}</h4>
                <p className="text-sm text-gray-400">{benefit.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Footer - Dark */}
      <footer className="py-12 px-4 bg-[#070b12] border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={DIRECTORY_LOGO} alt="AI Horse-Powered" className="w-10 h-10 rounded-lg" />
                <span className="font-semibold text-white">AI Horse-Powered</span>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                The AI-powered local business directory. Find, review, and connect with the best businesses.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-white mb-4 text-sm">Directory</h4>
              <ul className="space-y-2.5 text-sm text-gray-500">
                <li><Link to="/directory" className="hover:text-white transition-colors">All Businesses</Link></li>
                <li><Link to="/directory/deals" className="hover:text-white transition-colors">Deals & Coupons</Link></li>
                <li><Link to="/directory/jobs" className="hover:text-white transition-colors">Job Board</Link></li>
                <li><Link to="/directory/classifieds" className="hover:text-white transition-colors">Classifieds</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-white mb-4 text-sm">For Businesses</h4>
              <ul className="space-y-2.5 text-sm text-gray-500">
                <li><Link to="/register" className="hover:text-white transition-colors">List Your Business</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Get AI Certified</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Affiliate Programs</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">Advertise</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-white mb-4 text-sm">Company</h4>
              <ul className="space-y-2.5 text-sm text-gray-500">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Enter</Link></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Join Community</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-white/5 text-center text-sm text-gray-600">
            © {new Date().getFullYear()} AI Horse-Powered Technology. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
